import { useEffect, useRef, useState } from 'react';
import { useModalDialog } from '@/pages/fulfillment/components/useModalDialog';
import { ProductImageZoom } from '@/pages/fulfillment/components/ProductImageZoom';
import type { PurchaseOrderCandidate } from '@/infrastructure/FulfillmentAdminClient';

export interface PurchaseOrderLookupModalProps {
  /** The code the desk scanned and nothing placed; it is the tracking number a match registers. */
  code: string;
  busy: boolean;
  onLookup: (orderNo: string) => Promise<PurchaseOrderCandidate[]>;
  onMatch: (candidate: PurchaseOrderCandidate) => void;
  /** Hands the parcel on to the pool, carrying the number that failed when there was one. */
  onSendToPool: (orderNo: string | null) => void;
  onClose: () => void;
}

/** How a product reads on every fulfillment screen: brand first, Korean underneath [G-6]. */
const productLabel = (candidate: PurchaseOrderCandidate): string =>
  [candidate.brand, candidate.productName].filter(Boolean).join(' ') || candidate.sku;

/** One lookup is in one of these at a time; the body paints exactly one of them. */
type Outcome =
  | { kind: 'idle' }
  | { kind: 'looking'; orderNo: string }
  | { kind: 'found'; orderNo: string; candidates: PurchaseOrderCandidate[] }
  | { kind: 'missed'; orderNo: string }
  | { kind: 'failed'; orderNo: string };

/**
 * M2 — the order-number lookup a desk gets when a scanned barcode placed nothing [L-M2]. The operator
 * can still read the purchase order number off the parcel, and matching a row registers the scanned
 * code onto that order line, so rescanning the same barcode resolves normally [BR-27]. Only a lookup
 * that finds nothing hands the parcel on to the pool.
 *
 * The layout reads top to bottom the way the desk works: the barcode in hand, the one number that can
 * place it, the lines it placed. A lookup that is running, that found nothing, or that could not run
 * each takes the same slot, so the eye never has to look for the answer.
 */
export function PurchaseOrderLookupModal({
  code,
  busy,
  onLookup,
  onMatch,
  onSendToPool,
  onClose,
}: PurchaseOrderLookupModalProps) {
  const [orderNo, setOrderNo] = useState('');
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'idle' });
  const dialogRef = useModalDialog(onClose);
  const orderNoRef = useRef<HTMLInputElement>(null);
  /** Only the newest lookup may paint; an earlier one landing late would offer rows from another order. */
  const attempt = useRef(0);

  // The operator's hands are already on the keyboard; the number is the only thing this modal wants.
  useEffect(() => orderNoRef.current?.focus(), []);

  const lookUp = async () => {
    const entered = orderNo.trim();
    if (entered.length === 0) {
      orderNoRef.current?.focus();
      return;
    }
    if (outcome.kind === 'looking' && outcome.orderNo === entered) {
      return;
    }
    const mine = ++attempt.current;
    setOutcome({ kind: 'looking', orderNo: entered });
    try {
      const found = await onLookup(entered);
      if (mine === attempt.current) {
        setOutcome(
          found.length > 0
            ? { kind: 'found', orderNo: entered, candidates: found }
            : { kind: 'missed', orderNo: entered },
        );
      }
    } catch {
      // An outage is not a confirmed miss: routing a valid order to the pool would lose the parcel.
      if (mine === attempt.current) {
        setOutcome({ kind: 'failed', orderNo: entered });
      }
    }
  };

  return (
    <dialog className="vo-modal pol" ref={dialogRef} aria-label="Barcode Not Recognized">
      <header>
        <b>Barcode Not Recognized</b>
        <button type="button" className="x" aria-label="Close" onClick={onClose}>
          ✕
        </button>
      </header>
      <div className="body pol-body">
        <div className="pol-scan" role="group" aria-label="Scanned barcode">
          <span className="pol-bars" aria-hidden="true" />
          <div className="pol-scan-text">
            <span className="pol-k">Scanned barcode</span>
            <code className="pol-code">{code}</code>
          </div>
          <span className="pol-tag">Not on any order</span>
        </div>

        <p className="pol-lead">
          Type the Coupang purchase order number printed on the parcel. Matching a line registers this barcode as its
          tracking number, so the next scan goes straight through.
        </p>

        <div className="pol-field">
          <label htmlFor="pol-order-no">Order No</label>
          <div className="pol-row">
            <input
              id="pol-order-no"
              ref={orderNoRef}
              aria-label="Order No"
              placeholder="Purchase order number on the parcel"
              autoComplete="off"
              spellCheck={false}
              value={orderNo}
              onChange={(event) => {
                setOrderNo(event.target.value);
                // A number being edited belongs to no result: the rows on screen were placed by the old one,
                // and a lookup still in flight for it must not paint either [SSD-6362].
                if (outcome.kind !== 'idle') {
                  attempt.current += 1;
                  setOutcome({ kind: 'idle' });
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void lookUp();
                }
              }}
            />
            <button type="button" className="btn btn-blue" disabled={busy} onClick={() => void lookUp()}>
              Look up
            </button>
          </div>
        </div>

        <div className="pol-outcome">
          {outcome.kind === 'looking' && (
            <div className="pol-state pol-looking" aria-live="polite" aria-busy="true">
              <span className="pol-spin" aria-hidden="true" />
              Looking up order number {outcome.orderNo}…
            </div>
          )}

          {outcome.kind === 'found' && (
            <div className="pol-found">
              <p className="pol-found-head" aria-live="polite">
                Order number {outcome.orderNo} matched {outcome.candidates.length}{' '}
                {outcome.candidates.length === 1 ? 'product line' : 'product lines'}. Press Match Tracking No on the
                line of the product you are holding.
              </p>
              <ul className="pol-cands" aria-label="Matching product lines">
                {outcome.candidates.map((candidate) => (
                  <li key={`${candidate.orderId}-${candidate.lineNo}`} className="pol-cand">
                    <div className="pol-thumb">
                      {candidate.imageUrl ? (
                        <ProductImageZoom src={candidate.imageUrl} label={productLabel(candidate)} className="is-fill">
                          <img src={candidate.imageUrl} alt={productLabel(candidate)} width={48} height={48} />
                        </ProductImageZoom>
                      ) : (
                        <span className="pol-thumb-empty" aria-hidden="true" />
                      )}
                    </div>
                    <div className="pol-info">
                      <b>{productLabel(candidate)}</b>
                      {candidate.productNameKr && <div className="pol-kr">{candidate.productNameKr}</div>}
                      <div className="pol-meta">
                        <span>Order {candidate.orderId}</span>
                        <span>Line {candidate.lineNo}</span>
                        <span>Qty {candidate.quantity}</span>
                      </div>
                    </div>
                    <div className="pol-act">
                      {candidate.trackingNumber ? (
                        <span className="pol-registered">
                          <span>Already registered</span>
                          <code>{candidate.trackingNumber}</code>
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-green btn-sm"
                          disabled={busy}
                          onClick={() => onMatch(candidate)}
                        >
                          Match Tracking No
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              <p className="pol-foot-note">
                Matching closes this window; the same barcode then scans straight through.
              </p>
            </div>
          )}

          {outcome.kind === 'missed' && (
            <div className="pol-state pol-missed" aria-live="polite">
              <p>
                No products match the entered order number {outcome.orderNo}. It may be a typo, or a number from another
                channel.
              </p>
              <div className="pol-state-actions">
                <button type="button" className="btn btn-line btn-sm" onClick={() => onSendToPool(outcome.orderNo)}>
                  Send to unrecognized pool
                </button>
                <span className="pol-hint">Opens the pool form with this number filled in.</span>
              </div>
            </div>
          )}

          {outcome.kind === 'failed' && (
            <div className="pol-state pol-failed" role="alert">
              <p>The order number could not be searched. Try again before sending the parcel to the pool.</p>
              <div className="pol-state-actions">
                <button type="button" className="btn btn-line btn-sm" disabled={busy} onClick={() => void lookUp()}>
                  Try again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <footer className="pol-footer">
        <div className="pol-footer-left">
          <span className="pol-hint">No number on the parcel?</span>
          <button type="button" className="btn btn-line btn-sm" onClick={() => onSendToPool(null)}>
            No order number
          </button>
        </div>
        <button type="button" className="btn btn-sm pol-cancel" onClick={onClose}>
          Cancel
        </button>
      </footer>
    </dialog>
  );
}
